import { BasePresenter } from "core/presenter";
import { UserRepository, NewSaleReportRepository } from "core/repository";

import Moment from "moment";
import * as FileSaver from "file-saver";

import { fn } from "util";
import { DocumentTypes, SaleStatus } from "core/enum";

class SalesmanSalesReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day")
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      summary: {
        total_revenues: 0,
        total_costs: 0,
        profit: 0
      },
      results: [],
      order: {
        field: "name",
        direction: "ASC"
      },
      userSearchOptions: {
        online: true,
        repository: UserRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.getUserFilters(filter)
      },
      user: {
        name: "",
        uuid: null
      },
      mode: "user"
    };
  }

  async onStart() {
    this.load();
  }

  msgError(content) {
    this.sendNotification({
      type: "error",
      title: "Oops",
      content: content
    });
  }

  makePagination(perPage, total) {
    this.viewModel.pagination = {
      ...this.viewModel.pagination,
      total: total,
      perPage: perPage
    };
  }

  async load() {
    this.viewModel.loading = true;
    this.viewModel.results = [];

    let resultsPromise;
    if (this.viewModel.user.uuid) {
      this.viewModel.mode = "sale";
      resultsPromise = this.loadSaleResults();
    } else {
      this.viewModel.mode = "user";
      resultsPromise = this.loadUserResults();
    }

    await Promise.all([resultsPromise, this.loadSummary(this.viewModel.mode)]);

    this.viewModel.loading = false;
  }

  cleanResults() {
    this.viewModel.results = [];
  }

  async loadUserResults() {
    this.renewCancelToken();
    this.cleanResults();
    const filters = await this.getReportFilters();

    const repositoryResult = await NewSaleReportRepository.getReportSalesmanResults(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.salesmans;
      const perPage = repositoryResult.data.pagination.per_page;
      const total = repositoryResult.data.pagination.total_items;
      this.makePagination(perPage, total);
    } else if (!repositoryResult.userCancelled) {
      this.msgError(
        "Não foi possível carregar os vendedores.\nTente novamente mais tarde ou entre em contato com o suporte."
      );
    }
  }

  async loadSaleResults() {
    this.renewCancelToken();
    this.cleanResults();
    const filters = await this.getReportFilters();

    const repositoryResult = await NewSaleReportRepository.getReportSalesmanSalesResults(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.sales;
      const perPage = repositoryResult.data.pagination.per_page;
      const total = repositoryResult.data.pagination.total_items;
      this.makePagination(perPage, total);
    } else if (!repositoryResult.userCancelled) {
      this.msgError(
        "Não foi possível carregar as vendas do vendedor selecionado.\nTente novamente mais tarde ou entre em contato com o suporte."
      );
    }
  }

  cleanSummary() {
    this.viewModel.summary.total_revenues = 0;
    this.viewModel.summary.total_costs = 0;
    this.viewModel.summary.profit = 0;
  }

  async loadSummary(user) {
    this.cleanSummary();
    const filters = await this.getReportFilters();

    if (user === "sale") {
      const repositoryResult = await NewSaleReportRepository.getReportSalesmanResults(
        filters
      );
      if (repositoryResult.success && repositoryResult.data.salesmans.length) {
        this.viewModel.summary.total_revenues =
          repositoryResult.data.salesmans[0].revenues_value;
        this.viewModel.summary.total_costs =
          repositoryResult.data.salesmans[0].costs_value;
        this.viewModel.summary.profit =
          repositoryResult.data.salesmans[0].profit;
      }
    } else {
      const repositoryResult = await NewSaleReportRepository.getReportSalesmanSummary(
        filters
      );

      if (repositoryResult.success) {
        this.viewModel.summary = repositoryResult.data;
      }
    }
  }

  async getReportFilters() {
    const { user, filters, pagination } = this.viewModel;
    const { dateFrom, dateTo } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;
    const order = this.getOrder();
    const reportFilters = {
      company_uuid: company_uuid,
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      page: pagination.page,
      user: user.uuid,
      order: order.direction === "DESC" ? `-${order.field}` : order.field
    };

    return reportFilters;
  }

  getUserFilters(query) {
    const filters = [];

    if (query) {
      filters.push({
        connector: "AND",
        field: "name",
        operation: "LIKE",
        value: query
      });
    }

    return filters;
  }

  async onDownload(format) {
    this.viewModel.loading = true;
    if (format === "csv") {
      await this._downloadCSV();
    } else {
      const content = "Ainda não suportamos esse formato";
      this.msgError(content);
    }
    this.viewModel.loading = false;
  }

  formatDocumentType(type) {
    return DocumentTypes[type];
  }

  async _loadUserResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    filters.count = 9999;
    const repositoryResult = await NewSaleReportRepository.getReportSalesmanResults(
      filters
    );

    if (repositoryResult.success) {
      return repositoryResult.data.salesmans;
    }
  }

  async _loadSaleResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    filters.count = 9999;
    const repositoryResult = await NewSaleReportRepository.getReportSalesmanSalesResults(
      filters
    );

    if (repositoryResult.success) {
      return repositoryResult.data.sales;
    }
  }

  async _downloadCSV() {
    let { mode } = this.viewModel;
    let content,
      results = [];

    if (mode === "user") {
      results = await this._loadUserResultsDownload();
      content = [
        "Vendedor; Quantidade de Compras; Faturamento; Custos; Lucro Bruto"
      ];
      results.forEach(item => {
        const revenuesValue = fn.centsToReal(item.revenues_value);
        const costValue = fn.centsToReal(item.costs_value);
        const profitValue = fn.centsToReal(item.profit);
        content.push(
          `${item.salesman_name};  ${item.sales_count};${revenuesValue};${costValue};${profitValue}`
        );
      });
      const contentBlob = new Blob([content.join("\n")], {
        type: "application/csv;charset=utf-8"
      });
      FileSaver.saveAs(contentBlob, "relatorio-de-vendas-por-vendedor.csv");
    } else {
      results = await this._loadSaleResultsDownload();
      content = [
        "Data; Tipo do Documento; Numero do Documento; Status; Faturamento; Custos; Lucro"
      ];
      results.forEach(item => {
        const revenuesValue = fn.centsToReal(item.revenues_value);
        const costValue = fn.centsToReal(item.costs_value);
        const profitValue = fn.centsToReal(item.profit);
        const date = Moment(item.activated_at).format("DD/MM/YYYY HH:mm");
        content.push(
          `${date}; ${this.formatDocumentType(item.type)};${item.number};${
            SaleStatus[item.status]
          };${revenuesValue};${costValue};${profitValue}`
        );
      });
      const contentBlob = new Blob([content.join("\n")], {
        type: "application/csv;charset=utf-8"
      });
      FileSaver.saveAs(
        contentBlob,
        `relatorio-de-vendas-por-vendedor-${this.viewModel.user.name}.csv`
      );
    }
  }

  getOrder() {
    const { order } = this.viewModel;

    if (this.viewModel.mode === "sale" && order.field === "document_number") {
      return { direction: order.direction, field: "document.number" };
    }

    return order;
  }

  onSelectUser(selectedUser) {
    if (selectedUser.name === "") {
      this.viewModel.user = selectedUser;
      this.viewModel.mode = "sale";
      this.load();
    }
    this.viewModel.user = selectedUser;
    this.viewModel.pagination.page = 1;

    if (selectedUser.uuid) {
      this.viewModel.order = { field: "sale.activated_at", direction: "ASC" };
    }

    this.load();
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (filters[key] !== this.viewModel.filters[key]) {
        this.viewModel.filters = {
          ...this.viewModel.filters,
          ...filters
        };
        this.viewModel.pagination.page = 1;

        this.load();
        break;
      }
    }
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }
}

export default SalesmanSalesReportPresenter;



// WEBPACK FOOTER //
// ./src/components/app/management/salesman-sales-report/presenter.js